import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { toast } from 'sonner';
import { useEngine } from '@/contexts/EngineContext';
import { runProgramSwitch, clearPlanCaches, SWITCH_ERROR_MESSAGE } from '@/utils/programSwitch';
import { ProgramPhaseBar } from '@/components/ProgramPhaseBar';

const NUDGE_KEY = 'bm_program_carousel_nudged';
const CUSTOM_TYPE = 'custom';
const COACH_TYPE = 'coach';
const COACH_HERO = '/program-coach-built.webp';

const BUILD_OWN_HERO = '/program-custom-build.webp';

const CARD_SHADOW = { boxShadow: '0 18px 50px rgba(0,0,0,0.45)' };

/** Per-preset copy. Anything unlisted falls back to a generic line. */
const PRESET_COPY = {
  'booty-max': {
    tag: 'glute focus',
    blurb: 'Glute-focused plan that builds around you and the equipment you actually have.',
  },
  'muscle-max': {
    tag: 'muscle build',
    blurb: 'Progressive overload built from your muscle-building goal, your equipment and your age.',
  },
};

/** 'Muscle Max Program' -> 'muscle-max' (stable testid + copy key). */
const presetSlug = (name = '') =>
  name.toLowerCase().replace(/\bprogram\b/g, '').trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
  || 'preset';

/** Coach, then every curated program, then Build Your Own. Swiping here must not
 *  change tabs, hence the data-no-swipe wrapper SwipeNavigator already honours. */
export const ProgramCarousel = ({
  api,
  presetName = 'Booty Max Program',
  mode = 'act',          // 'act' = open/start the program, 'select' = report the choice
  onSelect,              // (choice) => void, choice is 'coach' | 'custom' | `preset:{id}`
  selectedValue,         // highlights the matching card in select mode
  layout = 'carousel',   // 'carousel' | 'stack'
}) => {
  const [presets, setPresets] = useState([]);
  const [options, setOptions] = useState([]);
  const [busy, setBusy] = useState(null);
  const engine = useEngine();
  const navigate = useNavigate();
  const [emblaApi, setEmblaApi] = useState(null);
  const [selected, setSelected] = useState(0);
  const [slideCount, setSlideCount] = useState(2);
  const nudgeRef = useRef(null);

  // Track the active slide for the position dots.
  useEffect(() => {
    if (!emblaApi) return;
    const sync = () => {
      setSelected(emblaApi.selectedScrollSnap());
      setSlideCount(emblaApi.scrollSnapList().length);
    };
    sync();
    emblaApi.on('select', sync);
    emblaApi.on('reInit', sync);
    return () => {
      emblaApi.off('select', sync);
      emblaApi.off('reInit', sync);
    };
  }, [emblaApi]);

  // One-time nudge: drift toward the next card and spring back so it reads as
  // swipeable. Animates our own wrapper - embla owns the transform on its container.
  useEffect(() => {
    if (!emblaApi) return;
    let cancelled = false;
    try {
      if (localStorage.getItem(NUDGE_KEY)) return;
    } catch {
      return;
    }
    const node = nudgeRef.current;
    if (!node) return;
    const timers = [
      setTimeout(() => {
        if (cancelled) return;
        node.style.transition = 'transform 520ms cubic-bezier(0.22, 1, 0.36, 1)';
        node.style.transform = 'translate3d(-56px, 0, 0)';
      }, 1600),
      setTimeout(() => {
        if (cancelled) return;
        node.style.transform = 'translate3d(0, 0, 0)';
      }, 2250),
      setTimeout(() => {
        if (cancelled) return;
        node.style.transition = '';
        node.style.transform = '';
        try { localStorage.setItem(NUDGE_KEY, '1'); } catch {}
      }, 2900),
    ];
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      node.style.transition = '';
      node.style.transform = '';
    };
  }, [emblaApi]);

  const scrollToSlide = useCallback((index) => emblaApi?.scrollTo(index), [emblaApi]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [presetsRes, programsRes] = await Promise.all([
          api.get('/curated-presets'),
          api.get('/programs/available').catch(() => null),
        ]);
        if (cancelled) return;
        const list = presetsRes.data?.presets || [];
        // Oldest first: Booty Max, then Muscle Max, then anything added later.
        setPresets([...list].sort((a, b) => String(a.created_at || '').localeCompare(String(b.created_at || ''))));
        if (programsRes) setOptions(programsRes.data?.options || []);
      } catch {
        // presets are non-critical for the dashboard
      }
    })();
    return () => { cancelled = true; };
  }, [api]);

  const optionFor = (programType) => options.find((o) => o.program_type === programType);

  /** Already on it -> just open it. Saved -> resume it. New -> start it. */
  const open = async (programType, label) => {
    if (busy) return;
    const option = optionFor(programType);
    if (option?.is_current) {
      window.location.href = '/workout';
      return;
    }
    setBusy(programType);
    const switchFn = async () => {
        if (option?.saved) {
          const result = await runProgramSwitch(api, { target: programType });
          if (!result.switched) throw new Error(SWITCH_ERROR_MESSAGE);
          clearPlanCaches();
          return { message: `Back on ${label} where you left off` };
        }
        if (programType === CUSTOM_TYPE) {
          if (!option?.can_start) return { redirect: '/custom-builder' };
          const result = await runProgramSwitch(api, { target: programType });
          if (result.needs_builder) return { redirect: '/custom-builder' };
          if (!result.switched) throw new Error(SWITCH_ERROR_MESSAGE);
          clearPlanCaches();
          return { message: `${label} is ready` };
        }
        if (programType === COACH_TYPE) {
          const result = await runProgramSwitch(api, { target: COACH_TYPE });
          if (!result.switched) throw new Error(SWITCH_ERROR_MESSAGE);
          clearPlanCaches();
          return { message: `${label} is ready` };
        }
        await api.post(`/curated-presets/${programType.slice('preset:'.length)}/start`);
        clearPlanCaches();
        return { message: `${label} started — your plan is ready` };
    };
    engine.trigger({
      subtitle: `Building ${label}`,
      mode: 'change',
      generationFn: switchFn,
      onRetry: switchFn,
      onSuccess: (data) => {
        setBusy(null);
        if (data?.redirect === '/custom-builder') {
          window.location.href = '/custom-builder';
          return;
        }
        if (data?.message) toast.success(data.message);
        navigate('/workout');
      },
      onError: (err) => {
        setBusy(null);
        if (err?.response?.data?.needs_builder) {
          window.location.href = '/custom-builder';
          return;
        }
        toast.error(SWITCH_ERROR_MESSAGE);
      },
      onDismiss: () => setBusy(null),
    });
  };

  const ctaLabel = (programType, fresh) => {
    const option = optionFor(programType);
    if (option?.is_current) return 'Continue';
    if (option?.saved) return 'Resume';
    return fresh;
  };

  const selectMode = mode === 'select';
  const asStack = layout === 'stack';

  /** act mode: open/start the program. select mode: just report the choice. */
  const activate = (programType, label) => {
    if (selectMode) {
      onSelect?.(programType);
      return;
    }
    open(programType, label);
  };

  const cardCta = (programType, fresh) => {
    if (!selectMode) return ctaLabel(programType, fresh);
    return selectedValue === programType ? 'Selected' : 'Choose This';
  };

  const selectedCls = (programType) => (
    selectMode && programType && selectedValue === programType
      ? ' ring-2 ring-accent ring-offset-4 ring-offset-background rounded-xl'
      : ''
  );

  const cards = [
    {
      key: 'coach',
      node: (
        <div className={`h-full flex flex-col${selectedCls(COACH_TYPE)}`} data-testid="coach-card">
          <img
            src={COACH_HERO}
            alt="Coach builds your program"
            className="w-full aspect-square object-cover object-center rounded-2xl border border-white/[0.08]"
            style={CARD_SHADOW}
            width={1024}
            height={1024}
            loading="lazy"
            decoding="async"
          />
          <h3 className="mt-3.5 font-heading font-black uppercase tracking-tight text-base text-white" data-testid="coach-title">
            Coach Built
          </h3>
          <p className="mt-1 text-xs font-heading font-bold uppercase tracking-[0.14em] text-accent">
            Adapts every week to you
          </p>
          <p className="mt-2 mb-4 text-sm text-white/55 leading-snug" data-testid="coach-blurb">
            Your coach plans every session and adjusts it as your strength and recovery change.
          </p>
          <button
            onClick={() => activate(COACH_TYPE, 'Your Coach program')}
            disabled={!!busy}
            aria-pressed={selectMode ? selectedValue === COACH_TYPE : undefined}
            className="w-full py-2.5 rounded-xl font-heading font-bold uppercase text-[13px] tracking-wider text-accent-foreground active:scale-[0.97] transition-transform disabled:opacity-60 flex items-center justify-center gap-2"
            style={{ background: 'var(--accent-hex)', marginTop: 'auto', boxShadow: '0 0 18px rgba(var(--accent-rgb),0.35)' }}
            data-testid="coach-start-btn"
          >
            {busy === COACH_TYPE && <Loader2 className="w-4 h-4 animate-spin" />}
            {cardCta(COACH_TYPE, 'Let Coach Build')}
          </button>
        </div>
      ),
    },
    ...presets.map((p) => {
      const slug = presetSlug(p.name || presetName);
      const type = `preset:${p.id}`;
      const copy = PRESET_COPY[slug] || {};
      const weeks = p.duration_weeks || 12;
      return {
        key: type,
        node: (
          <div className={`h-full flex flex-col${selectedCls(type)}`} data-testid={`${slug}-card`}>
            {p.hero_image_url ? (
              <img
                src={p.hero_image_url}
                alt={p.name || presetName}
                className="w-full aspect-square object-cover object-center rounded-2xl border border-accent/30"
                style={CARD_SHADOW}
                width={1024}
                height={1024}
                loading="lazy"
                decoding="async"
              />
            ) : (
              <div
                className="w-full aspect-square flex items-center justify-center text-xs uppercase tracking-[0.2em] text-white/35 rounded-2xl border border-accent/30"
                style={{ ...CARD_SHADOW, background: 'linear-gradient(135deg, #1b1b1b, #0d0d0d)' }}
                data-testid={`${slug}-hero-placeholder`}
              >
                Hero image coming soon
              </div>
            )}
            <h3 className="mt-3.5 font-heading font-black uppercase tracking-tight text-base text-white" data-testid={`${slug}-title`}>
              {p.name || presetName}
            </h3>
            <p className="mt-1 text-xs font-heading font-bold uppercase tracking-[0.14em] text-accent" data-testid={`${slug}-duration`}>
              {weeks} weeks{copy.tag ? ` · ${copy.tag}` : ''}
            </p>
            <p className="mt-2 mb-4 text-sm text-white/55 leading-snug" data-testid={`${slug}-blurb`}>
              {copy.blurb || `${weeks}-week program built around your goal, your equipment and your level.`}
            </p>
            <button
              onClick={() => activate(type, p.name || presetName)}
              disabled={!!busy}
              aria-pressed={selectMode ? selectedValue === type : undefined}
              className="w-full py-2.5 rounded-xl font-heading font-bold uppercase text-[13px] tracking-wider text-accent-foreground active:scale-[0.97] transition-transform disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ background: 'var(--accent-hex)', marginTop: 'auto', boxShadow: '0 0 18px rgba(var(--accent-rgb),0.35)' }}
              data-testid={`${slug}-start-btn`}
            >
              {busy === type && <Loader2 className="w-4 h-4 animate-spin" />}
              {cardCta(type, 'Start Program')}
            </button>
          </div>
        ),
      };
    }),
    {
      key: 'custom',
      node: (
        <div className={`h-full flex flex-col${selectedCls(CUSTOM_TYPE)}`} data-testid="build-own-card">
          <img
            src={BUILD_OWN_HERO}
            alt="Build your own program"
            className="w-full aspect-square object-cover object-center rounded-2xl border border-white/[0.08]"
            style={CARD_SHADOW}
            width={1024}
            height={1024}
            loading="lazy"
            decoding="async"
          />
          <h3 className="mt-3.5 font-heading font-black uppercase tracking-tight text-base text-white" data-testid="build-own-title">
            Build Your Own
          </h3>
          <p className="mt-1 text-xs font-heading font-bold uppercase tracking-[0.14em] text-accent">
            Your exercises · your schedule
          </p>
          <p className="mt-2 mb-4 text-sm text-white/55 leading-snug" data-testid="build-own-blurb">
            Pick the moves you love — your coach still sets the reps, rest and progression.
          </p>
          <button
            onClick={() => activate(CUSTOM_TYPE, 'Your custom program')}
            disabled={!!busy}
            aria-pressed={selectMode ? selectedValue === CUSTOM_TYPE : undefined}
            className="w-full py-2.5 rounded-xl font-heading font-bold uppercase text-[13px] tracking-wider text-accent-foreground active:scale-[0.97] transition-transform disabled:opacity-60 flex items-center justify-center gap-2"
            style={{ background: 'var(--accent-hex)', marginTop: 'auto', boxShadow: '0 0 18px rgba(var(--accent-rgb),0.35)' }}
            data-testid="build-own-btn"
          >
            {busy === CUSTOM_TYPE && <Loader2 className="w-4 h-4 animate-spin" />}
            {cardCta(CUSTOM_TYPE, 'Start Building')}
          </button>
        </div>
      ),
    },
  ];

  // Stack: same cards, vertical list, no swipe/dots/nudge.
  if (asStack) {
    return (
      <div data-no-swipe data-testid="program-carousel" className="space-y-7">
        {cards.map((c) => (
          <div key={c.key}>{c.node}</div>
        ))}
      </div>
    );
  }

  return (
    <div data-no-swipe data-testid="program-carousel">
      <div ref={nudgeRef} style={{ willChange: 'transform' }}>
      <Carousel opts={{ align: 'start', loop: false, containScroll: 'trimSnaps' }} setApi={setEmblaApi}>
        <CarouselContent className="-ml-3 items-stretch">
          {/* basis < 100% leaves a sliver of the next card visible */}
          {cards.map((c) => (
            <CarouselItem key={c.key} className="pl-3 basis-[88%]">
              {c.node}
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious
          className="hidden md:flex left-1 h-11 w-11 bg-[#141414]/90 border-accent/40 text-accent hover:bg-accent hover:text-accent-foreground"
          data-testid="program-carousel-prev"
        />
        <CarouselNext
          className="hidden md:flex right-1 h-11 w-11 bg-[#141414]/90 border-accent/40 text-accent hover:bg-accent hover:text-accent-foreground"
          data-testid="program-carousel-next"
        />
      </Carousel>
      </div>

      <div className="mt-3 flex items-center justify-center gap-2" data-testid="program-carousel-dots">
        {Array.from({ length: slideCount }).map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => scrollToSlide(i)}
            aria-label={`Go to program ${i + 1}`}
            className={`h-1.5 rounded-full transition-all ${
              i === selected ? 'w-6 bg-accent' : 'w-1.5 bg-white/25'
            }`}
            data-testid={`program-carousel-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
};

/** 'Session X of Y' strip for the active curated program. */
export const ProgramProgressStrip = ({ api }) => {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/curated-presets/active/status');
        // Always write the answer: leaving the preset must clear a stale strip.
        if (!cancelled) setStatus(res.data?.active ? res.data : null);
      } catch {
        // non-critical
      }
    })();
    return () => { cancelled = true; };
  }, [api]);

  if (!status?.target_sessions) return null;
  const pct = Math.min(100, Math.round((status.sessions_completed / status.target_sessions) * 100));
  const finished = status.sessions_completed >= status.target_sessions;

  return (
    <div
      className="rounded-2xl border border-accent/30 bg-[#101010] px-4 py-3.5"
      data-testid="program-progress-strip"
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-heading font-bold text-sm uppercase tracking-wide text-white truncate">
          {status.preset?.name || 'Curated Program'}
        </span>
        <span className="text-sm font-heading font-black text-accent shrink-0" data-testid="program-session-indicator">
          Session {status.sessions_completed} of {status.target_sessions}
        </span>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div className="h-full rounded-full bg-accent transition-all" style={{ width: `${pct}%` }} />
      </div>
      <p className="mt-2 text-xs text-white/45" data-testid="program-progress-note">
        {finished
          ? `All ${status.duration_weeks || 12} weeks done — you're still training this program`
          : `Week ${status.program_week || 1} of ${status.duration_weeks || 12} at your own pace, training less often takes longer`}
      </p>
      <ProgramPhaseBar
        programName={status.preset?.name}
        week={status.program_week}
        durationWeeks={status.duration_weeks}
        className="mt-3"
      />
    </div>
  );
};
